import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { BRAND } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { en: "VAT Services", ar: "ضريبة القيمة المضافة" },
  { en: "Corporate Tax", ar: "ضريبة الشركات" },
  { en: "Accounting & Bookkeeping", ar: "المحاسبة ومسك الدفاتر" },
  { en: "FTA Support", ar: "دعم الهيئة" },
  { en: "Financial Reporting", ar: "التقارير المالية" },
  { en: "eInvoicing Readiness", ar: "الفوترة الإلكترونية" },
  { en: "Other", ar: "أخرى" },
];

const emptyForm = { name: "", company: "", phone: "", email: "", service: "", message: "" };

export const ContactForm = () => {
  const { t, lang, isAr } = useLang();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error(t({ en: "Please add your name and phone.", ar: "يرجى إدخال الاسم ورقم الهاتف." }));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, service: form.service || SERVICE_OPTIONS[0].en, lang });
      toast.success(t({ en: "Thank you — we'll be in touch shortly.", ar: "شكراً لك — سنتواصل معك قريباً." }));
      setForm(emptyForm);
    } catch (err) {
      toast.error(t({ en: "Something went wrong. Please try WhatsApp.", ar: "حدث خطأ ما. يرجى المحاولة عبر واتساب." }));
    } finally {
      setLoading(false);
    }
  };

  const sendWhatsApp = () => {
    const lines = [
      `${isAr ? "الاسم" : "Name"}: ${form.name}`,
      form.company && `${isAr ? "الشركة" : "Company"}: ${form.company}`,
      form.phone && `${isAr ? "الهاتف" : "Phone"}: ${form.phone}`,
      form.email && `${isAr ? "البريد" : "Email"}: ${form.email}`,
      form.service && `${isAr ? "الخدمة" : "Service"}: ${form.service}`,
      form.message && `${isAr ? "التفاصيل" : "Details"}: ${form.message}`,
    ].filter(Boolean);
    const text = encodeURIComponent(`${isAr ? "طلب استشارة من موقع تنسيق" : "Consultation request from TANSEEQ website"}\n${lines.join("\n")}`);
    window.open(`https://wa.me/${BRAND.phoneRaw}?text=${text}`, "_blank");
  };

  const inputCls =
    "w-full h-12 px-4 rounded-xl border border-line bg-ivory text-ink placeholder:text-muted/50 focus:outline-none focus:border-navy focus:ring-4 focus:ring-navy/10 transition-[border-color,box-shadow]";
  const labelCls = "block text-sm font-medium text-ink/80 mb-2";

  return (
    <form onSubmit={submit} data-testid="contact-form" className="grid sm:grid-cols-2 gap-5">
      <div>
        <label className={labelCls}>{t({ en: "Name", ar: "الاسم" })} *</label>
        <input data-testid="contact-name" className={inputCls} value={form.name} onChange={set("name")} required />
      </div>
      <div>
        <label className={labelCls}>{t({ en: "Company", ar: "الشركة" })}</label>
        <input data-testid="contact-company" className={inputCls} value={form.company} onChange={set("company")} />
      </div>
      <div>
        <label className={labelCls}>{t({ en: "Phone", ar: "الهاتف" })} *</label>
        <input data-testid="contact-phone" className={inputCls} value={form.phone} onChange={set("phone")} dir="ltr" required />
      </div>
      <div>
        <label className={labelCls}>{t({ en: "Email", ar: "البريد الإلكتروني" })}</label>
        <input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={set("email")} dir="ltr" />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls}>{t({ en: "Service", ar: "الخدمة" })}</label>
        <select data-testid="contact-service" className={inputCls} value={form.service} onChange={set("service")}>
          {SERVICE_OPTIONS.map((o, i) => (<option key={i} value={o.en}>{t(o)}</option>))}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls}>{t({ en: "Brief details", ar: "تفاصيل مختصرة" })}</label>
        <textarea data-testid="contact-message" rows={4} className={inputCls + " h-auto py-3 resize-none"} value={form.message} onChange={set("message")} />
      </div>
      <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3">
        <button type="submit" disabled={loading} data-testid="contact-submit"
          className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-navy text-white font-semibold hover:bg-navy-600 transition-colors disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {t({ en: "Send request", ar: "إرسال الطلب" })}
        </button>
        <button type="button" onClick={sendWhatsApp} data-testid="contact-whatsapp"
          className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full border border-brandgreen text-brandgreen font-semibold hover:bg-brandgreen hover:text-white transition-colors">
          {t({ en: "Send via WhatsApp", ar: "الإرسال عبر واتساب" })}
        </button>
      </div>
    </form>
  );
};
